import { APP_INITIALIZER, ApplicationConfig, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { JITSI_API } from '@core/jitsi/jitsi-api';
import { JitsiAdapter } from '@core/jitsi/jitsi.adapter';
import { provideNativeBridge } from '@core/bridge/bridge.providers';
import {
  TRANSLATION_PROVIDER,
  UnavailableTranslationProvider,
} from '@core/translation/translation-provider';
import { WebSocketTranslationProvider } from '@core/translation/websocket-translation-provider';
import { LoggerService } from '@core/logging/logger.service';
import { VOICE_ENGINE } from '@core/voice/voice-engine';
import { ServerVoiceEngine } from '@core/voice/server-voice-engine';
import { AuthTokenService } from '@core/backend/auth-token.service';
import { apiAuthInterceptor } from '@core/backend/api-auth.interceptor';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    // The doctor's token is attached to DoctorConsole API requests only.
    provideHttpClient(withInterceptors([apiAuthInterceptor])),
    // Read the doctor console's `#token=…` before anything else runs: it is
    // taken off the URL once read, and the first API call needs it.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const auth = inject(AuthTokenService);
        return () => auth.captureFromUrl();
      },
    },
    // Both boundaries are provided by token rather than by concrete class, so a
    // test can substitute a fake without the application knowing. That is what
    // makes the toggle and teardown invariants testable in a browser.
    { provide: JITSI_API, useClass: JitsiAdapter },
    provideNativeBridge(),
    // Swapping the recognition engine is this one line (§8.4) — the audio
    // layer, the tap and the caption pipeline never learn which provider they
    // got. With no endpoint configured this reports unavailable, so the control
    // is not offered rather than being visible and failing when pressed.
    //
    // The session id comes from the logger rather than from `SessionService`.
    // That is not a style choice: `SessionService` injects `TranslationService`,
    // which injects this token, so reaching back for it here closes a cycle —
    // and Angular resolves a cycle by handing back a half-built instance whose
    // fields are still undefined. The logger is a leaf, holds the same id
    // (§9.1), and cannot form one.
    {
      provide: TRANSLATION_PROVIDER,
      useFactory: () => {
        if (!environment.translation.endpoint) {
          return new UnavailableTranslationProvider();
        }

        const logger = inject(LoggerService);
        return new WebSocketTranslationProvider({
          endpoint: environment.translation.endpoint,
          // Correlates the socket with the consultation across web, native and
          // server, so one call is a single queryable timeline (§9.1).
          sessionId: () => logger.getSessionId(),
        });
      },
    },
    // Translated voice (§8.9). Swapping the model is a server setting; swapping
    // how the browser reaches one is this line. `null` hides the control.
    {
      provide: VOICE_ENGINE,
      useFactory: () => {
        if (!environment.translation.endpoint || !environment.voiceTranslation.enabled) {
          return null;
        }
        const logger = inject(LoggerService);
        return new ServerVoiceEngine({
          endpoint: environment.translation.endpoint,
          sessionId: () => logger.getSessionId(),
        });
      },
    },
  ],
};
